import { LESSON_SEQUENCE_TYPES } from '@didaskill/domain/lessons/constants';

/**
 * Compute the sequence number of each sequence in the sequences list, and set it in the
 * `sequenceNumber` field.
 *
 * @param sequences - Sequences.
 * @param sequences[].isNumbered - Is the sequence numbered.
 * @returns Sequences with computed sequence number.
 */
export const computeSequenceNumbers = <T extends { isNumbered?: boolean }>(
  sequences?: T[],
): undefined | (T & { sequenceNumber?: number })[] => {
  let sequenceNumber = 1;

  return sequences?.map((sequence) => ({
    ...sequence,
    sequenceNumber: sequence.isNumbered ? sequenceNumber++ : undefined,
  }));
};

/**
 * Make sure that all text sequences have a title.
 *
 * @param sequences - Sequences.
 * @returns True if all text sequences have a title.
 */
export const allTextSequencesHaveTitle = (
  sequences: { type: LESSON_SEQUENCE_TYPES; content?: string; title?: string }[],
) =>
  sequences
    .filter(({ type }) => type === LESSON_SEQUENCE_TYPES.TEXT)
    .every((sequence) => sequence.content && sequence.title);

/**
 * Make sure that all training sequences have at least one question.
 *
 * @param sequences - Sequences.
 * @returns True if all training sequences fullfill requirements.
 */
export const allTrainingSequencesHaveQuestions = (
  sequences: {
    type: LESSON_SEQUENCE_TYPES;
    content?: string;
    exercise?: {
      questions: unknown[];
    };
  }[],
) =>
  sequences
    .filter(({ type }) => type === LESSON_SEQUENCE_TYPES.TRAINING)
    .every((sequence) => sequence?.exercise?.questions.length);

/**
 * Make sure that all activity sequences have a content package.
 * @param sequences - Sequences.
 * @returns True if all activity sequences have a content package.
 */
export const allActivitesSequencesHavePackage = (
  sequences: {
    type: LESSON_SEQUENCE_TYPES;
    content?: string;
    activity?: {
      contentPackage?: string;
    };
  }[],
) =>
  sequences
    .filter(({ type }) => type === LESSON_SEQUENCE_TYPES.ACTIVITY)
    .every((sequence) => sequence?.activity?.contentPackage);

/**
 * Check that all sequences are valid in order to publish editable version.
 *
 * @param sequences - Sequences.
 * @returns - Sequences are valid.
 */
export const areSequencesValidForPublishing = (
  sequences: {
    type: LESSON_SEQUENCE_TYPES;
    content?: string;
    title?: string;
    exercise?: {
      questions: unknown[];
    };
  }[],
) =>
  allTrainingSequencesHaveQuestions(sequences) &&
  allTextSequencesHaveTitle(sequences) &&
  allActivitesSequencesHavePackage(sequences);

/**
 * Whether every TEXT sequence has a PDF attachment (`contentFile` upload id).
 * @param sequences - Sequences.
 * @returns True if all text sequences have a PDF file.
 */
export function allSequencesHasPdfGenerated(
  sequences: { type: LESSON_SEQUENCE_TYPES; contentFile?: string | null }[],
): boolean {
  return sequences
    .filter(({ type }) => type === LESSON_SEQUENCE_TYPES.TEXT)
    .every((sequence) => Boolean(sequence.contentFile));
}
